/**
 * Meta Graph API Module
 *
 * Responsabilidade: Chamadas à Graph API do Facebook/Meta.
 * Busca contas de anúncio, pixels e páginas.
 */

import { MetaAdAccount, MetaPixel, MetaPage } from './metaTypes';

const GRAPH_API_VERSION = 'v18.0';
const GRAPH_API_BASE = `https://graph.facebook.com/${GRAPH_API_VERSION}`;

type GraphErrorBody = { error?: { message?: string } };
type GraphListBody<T> = GraphErrorBody & { data?: T[] };
type GraphMeBody = GraphErrorBody & { id?: string };
type GraphPageBody = GraphErrorBody & { business?: { id?: string } };
type GraphWabaBody = GraphErrorBody & { data?: Array<{ id?: string }> };

async function parseGraphJson<T>(response: Response): Promise<T> {
    return (await response.json()) as T;
}

/**
 * Busca as contas de anúncio disponíveis
 */
export const fetchMetaAdAccounts = async (accessToken: string): Promise<MetaAdAccount[]> => {
    try {
        const response = await fetch(
            `${GRAPH_API_BASE}/me/adaccounts?fields=id,name,account_id&limit=1000&access_token=${accessToken}`
        );
        const data = await parseGraphJson<GraphListBody<MetaAdAccount>>(response);

        if (data.error) {
            throw new Error(data.error.message);
        }

        return data.data || [];
    } catch (error) {
        console.error('[MetaAPI] Error fetching ad accounts:', error);
        throw error;
    }
};

/**
 * Busca os Pixels associados a uma conta de anúncio
 */
export const fetchMetaPixels = async (adAccountId: string, accessToken: string): Promise<MetaPixel[]> => {
    try {
        const cleanAccountId = adAccountId.startsWith('act_') ? adAccountId : `act_${adAccountId}`;
        const response = await fetch(
            `${GRAPH_API_BASE}/${cleanAccountId}/adspixels?fields=id,name&access_token=${accessToken}`
        );
        const data = await parseGraphJson<GraphListBody<MetaPixel>>(response);

        if (data.error) {
            console.warn('[MetaAPI] Erro ao buscar Pixels:', data.error.message);
            return [];
        }

        return data.data || [];
    } catch (error) {
        console.error('[MetaAPI] Error fetching pixels:', error);
        return [];
    }
};

/**
 * Busca as Páginas do Facebook do usuário
 */
export const fetchMetaPages = async (accessToken: string): Promise<MetaPage[]> => {
    try {
        const response = await fetch(
            `${GRAPH_API_BASE}/me/accounts?fields=id,name&limit=1000&access_token=${accessToken}`
        );
        const data = await parseGraphJson<GraphListBody<MetaPage>>(response);

        if (data.error) {
            console.warn('[MetaAPI] Erro ao buscar Páginas:', data.error.message);
            return [];
        }

        return data.data || [];
    } catch (error) {
        console.error('[MetaAPI] Error fetching pages:', error);
        return [];
    }
};

/**
 * Valida se o token ainda é válido
 */
export const validateMetaToken = async (accessToken: string): Promise<boolean> => {
    try {
        const response = await fetch(`${GRAPH_API_BASE}/me?access_token=${accessToken}`);
        const data = await parseGraphJson<GraphMeBody>(response);
        return !data.error;
    } catch {
        return false;
    }
};

/**
 * Busca WABA no Business Manager (opcional). Setup Uazapi usa ctwa_clid do webhook;
 * waba_id só melhora match na CAPI quando a conta oficial existir no BM.
 */
export const fetchWhatsAppBusinessAccount = async (
    pageId: string,
    accessToken: string
): Promise<string | null> => {
    try {
        const pageRes = await fetch(
            `${GRAPH_API_BASE}/${pageId}?fields=business{id}&access_token=${accessToken}`
        );
        const pageData = await parseGraphJson<GraphPageBody>(pageRes);
        if (pageData.error) {
            console.warn('[MetaAPI] WABA: página sem business vinculado:', pageData.error.message);
            return null;
        }

        const businessId = pageData.business?.id;
        if (!businessId) return null;

        for (const edge of ['owned_whatsapp_business_accounts', 'client_whatsapp_business_accounts']) {
            const wabaRes = await fetch(
                `${GRAPH_API_BASE}/${businessId}/${edge}?fields=id&limit=5&access_token=${accessToken}`
            );
            const wabaData = await parseGraphJson<GraphWabaBody>(wabaRes);
            const wabaId = wabaData.data?.[0]?.id;
            if (wabaId) return wabaId;
        }

        return null;
    } catch (error) {
        console.warn('[MetaAPI] WABA lookup failed:', error);
        return null;
    }
};

type MetaIntegrationConfig = { config?: Record<string, unknown> } | null;

/** Resolve waba_id da atribuição, integração ou Graph API. */
export async function resolveMetaWabaId(
    pageId: string | null,
    attribution: { waba_id?: string | null },
    integration: MetaIntegrationConfig
): Promise<string | null> {
    return attribution.waba_id || (integration?.config?.waba_id as string | undefined) || null;
}
